import { useRef, useState, type ReactNode } from 'react';
import { ArrowDown, Info, Loader2, Play, Square } from 'lucide-react';
import { TIP_BADGE } from './HoverTip';
import { PopMenu } from './PopMenu';
import { useReportBusy } from './phaseBusy';
import { useLanguage } from '../../contexts/LanguageContext';
import { IS_LOCKED } from '../../apiBase';

// A PRESS THAT IS NOT ONE MODEL CALL, AS A BADGE AMONG THE CALLS'. Two kinds: Phase 2's, which
// runs SEVERAL calls, and Phase 7's set placing, which runs none (it measures).
//
// PHASE 2'S: It was a big button over
// the row of call badges — a second way to run what each badge already runs, and the loudest
// thing on the phase. What it has that they do not is that it runs SEVERAL of them, in order,
// so that is what its menu says: Run, and under it the calls it makes.
//
// Named like the call badges (CallBadge's `named`), and with their (i): one look for every
// badge that opens onto a Run, whatever is behind it.
//
// Its Run row is in a portal on <body>, outside the phase's <fieldset disabled> (DemoLock), so
// it checks IS_LOCKED itself — like CallMenu's.
export function ChainBadge({ label, runLabel, steps = [], onRun, onStop, busy, disabled, progress }: {
  label: string;
  // "Run" unless the press is better named by what it does.
  runLabel?: string;
  // The calls the press makes, by the names their own badges carry, in the order they run.
  // None for a press that calls nothing, and the menu is then its Run row alone.
  steps?: string[];
  onRun: () => void;
  // STOP, in the Run row's place while the press runs — where given. No call is aborted (see
  // runHalt): the press stops sending new ones.
  onStop?: () => void;
  busy: boolean;
  disabled?: boolean;
  // Where the run is and how long it has been going — on the badge while it runs, since the
  // badge is the only place the press is.
  progress?: ReactNode;
}) {
  const { t } = useLanguage();
  useReportBusy(busy);
  const anchor = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        ref={anchor}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        title={label}
        className={`${TIP_BADGE} gap-1.5 pl-2.5`}
      >
        <span className="text-[10px] font-bold uppercase tracking-wider">{label}</span>
        {busy && progress && (
          <span className="text-[10px] font-normal tabular-nums opacity-60">{progress}</span>
        )}
        {busy
          ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
          : <Info className="h-3.5 w-3.5" />}
      </button>
      {open && (
        <PopMenu anchor={anchor} onClose={() => setOpen(false)}>
          <p className="px-4 pb-1 pt-1.5 text-center text-[10px] font-bold uppercase tracking-wider text-slate-900">
            {label}
          </p>
          {busy && onStop ? (
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onStop();
              }}
              className="flex w-full items-center justify-center gap-2 whitespace-nowrap px-4 py-2 text-xs font-semibold text-rose-600 transition-colors hover:bg-slate-100"
            >
              <Square className="h-3.5 w-3.5" />
              {t('notice.cat.stop')}
            </button>
          ) : (
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              onRun();
            }}
            disabled={disabled || busy || IS_LOCKED}
            className="flex w-full items-center justify-center gap-2 whitespace-nowrap px-4 py-2 text-xs font-semibold text-blue-600 transition-colors hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
            {runLabel ?? t('notice.tip.menuRun')}
          </button>
          )}
          {steps.length > 0 && (
          <div className="mt-1 border-t border-slate-100 px-4 pb-1.5 pt-2 text-center">
            <p className="mb-1.5 text-[11px] text-slate-500">{t('notice.chain.runs')}</p>
            <ol className="flex flex-col items-center">
              {steps.map((s, i) => (
                <li key={s} className="flex flex-col items-center">
                  {i > 0 && <ArrowDown className="my-0.5 h-3 w-3 text-slate-300" />}
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-700">{s}</span>
                </li>
              ))}
            </ol>
          </div>
          )}
        </PopMenu>
      )}
    </>
  );
}
